import { useEffect, useState } from "react";
import api, { apiError } from "../../../lib/api";
import { toast } from "sonner";

const EMPTY_BRAND = { logo_url: "", reply_to: "", sign_off_enabled: true, sign_off_greeting: "", sign_off_names: "Toby and Dipendra", sign_off_subtitle: "Solera Ventures" };

export function EmailStudio() {
  const [brand, setBrand] = useState(EMPTY_BRAND);
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);

  const loadPreview = (key) => {
    if (!key) return;
    setPreview(null);
    api.get(`/admin/email-templates/${key}/preview`).then((r) => setPreview(r.data)).catch((e) => toast.error(apiError(e)));
  };

  useEffect(() => {
    api.get("/admin/email-brand").then((r) => setBrand({ ...EMPTY_BRAND, ...r.data })).catch(() => {});
    api.get("/admin/email-templates").then((r) => {
      const list = r.data || [];
      setTemplates(list);
      if (list.length) { setSelected(list[0].key); loadPreview(list[0].key); }
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveBrand = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/email-brand", brand);
      setBrand({ ...EMPTY_BRAND, ...data });
      toast.success("Email branding saved. It applies to every email Solera sends.");
      loadPreview(selected);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const select = (key) => { setSelected(key); loadPreview(key); };

  const sendTest = async () => {
    if (!selected) return;
    setSending(true);
    try {
      const { data } = await api.post(`/admin/email-templates/${selected}/test`);
      toast.success(`Test email sent to ${data.sent_to}.`);
    } catch (e) { toast.error(apiError(e)); } finally { setSending(false); }
  };

  return (
    <div data-testid="admin-email-studio">
      <span className="t-label">Emails</span>
      <p className="t-small text-muted-s mt-2 measure">Preview every email Solera sends, send yourself a test, and set the branding that appears across all of them.</p>

      <div className="mt-8 grid lg:grid-cols-2 gap-12">
        <div className="max-w-md space-y-4" data-testid="email-brand-form">
          <span className="t-label">Branding</span>
          <label className="block">
            <span className="t-cap text-muted-s">Logo image URL. Leave blank to use the Solera wordmark. Use a hosted PNG on a light or transparent background.</span>
            <input className="input-solera mt-1 w-full" placeholder="https://…/logo.png" value={brand.logo_url} onChange={(e) => setBrand({ ...brand, logo_url: e.target.value })} data-testid="email-logo-input" />
          </label>
          <label className="block">
            <span className="t-cap text-muted-s">Reply-to address. Member replies go here instead of the sending address. Leave blank to use the sender.</span>
            <input className="input-solera mt-1 w-full" type="email" placeholder="contact@solera.vc" value={brand.reply_to} onChange={(e) => setBrand({ ...brand, reply_to: e.target.value })} data-testid="email-replyto-input" />
          </label>
          <label className="flex items-center gap-2 cursor-pointer pt-2">
            <input type="checkbox" checked={brand.sign_off_enabled} onChange={(e) => setBrand({ ...brand, sign_off_enabled: e.target.checked })} data-testid="email-signoff-enabled-checkbox" />
            <span className="t-cap text-muted-s">Sign every member-facing email with the names below</span>
          </label>
          <label className="block">
            <span className="t-cap text-muted-s">Greeting line above the names (optional), e.g. "Warm regards,"</span>
            <input className="input-solera mt-1 w-full" placeholder="Warm regards," value={brand.sign_off_greeting} onChange={(e) => setBrand({ ...brand, sign_off_greeting: e.target.value })} data-testid="email-signoff-greeting-input" />
          </label>
          <label className="block">
            <span className="t-cap text-muted-s">Sign-off names. Use both or a single name, e.g. "Toby and Dipendra" or "Dipendra".</span>
            <input className="input-solera mt-1 w-full" placeholder="Toby and Dipendra" value={brand.sign_off_names} onChange={(e) => setBrand({ ...brand, sign_off_names: e.target.value })} data-testid="email-signoff-names-input" />
          </label>
          <label className="block">
            <span className="t-cap text-muted-s">Line beneath the names (optional)</span>
            <input className="input-solera mt-1 w-full" placeholder="Solera Ventures" value={brand.sign_off_subtitle} onChange={(e) => setBrand({ ...brand, sign_off_subtitle: e.target.value })} data-testid="email-signoff-subtitle-input" />
          </label>
          <button className="btn-primary" disabled={busy} onClick={saveBrand} data-testid="email-brand-save">Save branding</button>
        </div>

        <div data-testid="email-preview-panel">
          <div className="flex items-center gap-3 flex-wrap">
            <select className="input-solera" value={selected} onChange={(e) => select(e.target.value)} data-testid="email-template-select">
              {templates.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
            <button className="btn-ghost" disabled={!selected || sending} onClick={sendTest} data-testid="email-send-test">Send test to me</button>
          </div>
          {preview && <p className="t-cap text-muted-s mt-3">Subject: {preview.subject}</p>}
          <div className="mt-3 border border-[#24272B]" style={{ height: "640px", background: "#FAFAF8" }}>
            {preview ? (
              <iframe title="email-preview" srcDoc={preview.html} style={{ width: "100%", height: "100%", border: 0 }} data-testid="email-preview-frame" />
            ) : (
              <div className="flex items-center justify-center h-full"><span className="t-label">Loading preview</span></div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
